/**
 * Text for the `rewardStatus` field that tournaments, daily tasks, challenges and
 * puzzle answers carry (contract: chess-backend-new docs/REWARD_STATUS_API.md).
 */
import { pluralRu } from "@utils/index";

export const DAY_WORDS = ["день", "дня", "дней"];

const REASON_TEXT = {
    // daily tasks and challenges are paid on the next progress recompute
    awaitingPayout: "Награда будет начислена при следующем обновлении",
    notFinished: "Турнир ещё идёт",
    // covers both "played outside the prizes" and "did not play"
    notPlaced: "Призы выданы, вас нет среди призёров (или вы не играли)",
    notRated: "Турнир без рейтинга, награды нет",
    variant: "Этот контроль времени не награждается",
    noStartDate: "Нет даты начала, награда не рассчитана",
    beforeCutoff: "До запуска наград",
    tooFewPlayers: "Слишком мало участников",
    noLevelMatch: "В названии турнира нет уровня",
    notCompleted: "Задание ещё не выполнено",
    levelNotEligible: "Награды начнутся, когда появится рейтинг",
    noAmount: "Для этого награды нет",
    notJoined: "Вы не участвуете",
    aboveCap: "Ваш уровень выше уровня челленджа",
    noUserLevel: "Уровень ещё не определён",
    unknownUserLevel: "Уровень ещё не определён",
    themeAlreadyCompleted: "За эту тему награда уже была",
    incorrect: "Неверное решение",
    alreadyRewarded: "Награда за эту задачу уже начислена",
    themeBelowLevel: "Задача ниже вашего уровня, без награды",
};

const FALLBACK_TEXT = {
    skipped: "Награда не начислена",
    pending: "Ожидает начисления",
};

/** Sum of the payouts of a "rewarded" status, 0 when it lists none. */
export const rewardTotal = (status) =>
    (Array.isArray(status?.rewards) ? status.rewards : [])
        .reduce((sum, r) => sum + (Number(r?.amount) || 0), 0);

/**
 * One line for a reward status: "+N" when paid, the reason otherwise.
 * null when there is no status (older backend) or the state is unknown, so the caller shows nothing.
 * `reasonText` overrides the wording of some reasons for one source (streak, level).
 * Object.hasOwn keeps a reason like "toString" from reading the prototype.
 */
export const rewardStatusText = (status, reasonText = {}) => {
    const state = status?.state;
    if (state === "rewarded") {
        return status.rewards?.length ? `+${rewardTotal(status)}` : "Начислено";
    }
    if (!Object.hasOwn(FALLBACK_TEXT, state)) return null;
    const texts = { ...REASON_TEXT, ...reasonText };
    const reason = status.reason;
    return reason && Object.hasOwn(texts, reason) ? texts[reason] : FALLBACK_TEXT[state];
};

/** Reason wording for a tournament: prizes are paid by the nightly job (around 04:00 UTC). */
export const TOURNAMENT_REASON_TEXT = {
    awaitingPayout: "Награда будет начислена ночью",
};

/** Reason wording for a "level reached" reward. */
export const LEVEL_REASON_TEXT = {
    levelNotEligible: "За этот уровень награды нет",
    notGranted: "Награда за этот уровень не начислялась",
};

/** Reason wording for a streak milestone `days` long, for a streak now `current` days long. */
export const streakReasonText = (days, current) => {
    const left = days - (Number(current) || 0);
    return {
        levelNotEligible: "Награды за серию начнутся, когда появится рейтинг",
        beforeCutoff: "Серия началась до запуска наград",
        awaitingPayout: "Награда скоро будет начислена",
        // a non-positive gap would be a backend mismatch: fall back to the generic pending text
        ...(left > 0 ? { notReached: `Ещё ${left} ${pluralRu(left, DAY_WORDS)} до порога` } : {}),
    };
};
